/*
  Bong Trendz — Cart Discount Widget engine (v3.0, 6 Sep 2026)

  WHY THIS FILE EXISTS (root cause of "blank on first add to cart" / "Apply
  button stuck disabled" bug reports, 6 Sep 2026):

  Previously this entire engine lived as an inline <script> inside
  snippets/bt-cart-promotions.liquid, rendered once per context ('page' on
  the cart page, 'drawer' inside the cart drawer). That snippet is only
  rendered in the drawer INSIDE `{% if cart != empty %}` (see
  snippets/cart-drawer.liquid). So on a fresh visit with an empty cart, the
  widget markup + its <script> do not exist in the page's initial HTML at
  all. The moment the customer adds their very first item, Dawn's
  cart-drawer.js opens the drawer and replaces #CartDrawer's contents via
  `.innerHTML = freshHtml` (Section Rendering API). Browsers never execute
  <script> tags that arrive via innerHTML — so on that very first add, the
  widget's script silently never ran. No MutationObserver was watching yet
  either, because the *only* place that observer used to get set up was
  inside that same never-executed script. Net effect: the widget div stayed
  permanently empty, the Apply button (which does not exist yet because
  nothing rendered) could not be interacted with, and nothing ever
  self-healed until a full page navigation happened to include the snippet
  in initial static HTML (cart already non-empty) — matching exactly what
  was reported: blank on the first add, fine again after a reload.

  FIX: move all rendering logic here, into a real external script (loaded
  via <script src> from theme.liquid — present in the static, NEVER-swapped
  page shell) so it is guaranteed to execute on every real page load. From
  the moment this file runs, it independently watches the DOM for both
  possible widget roots ('page' and 'drawer') and (re)renders whichever
  exist — including the very first time the drawer's markup appears via an
  AJAX swap, because the watching started at real page load, not inside the
  swappable content itself. bt-cart-promotions.liquid now only emits the
  <details> markup, its CSS and the JSON data island — no executable logic.

  v3.1 (6 Sep 2026) — BXGY eligibility fix:
  18. FIX: once cartProductIds finally started resolving correctly (after
     fixing the two Puja Ladder collections' missing Online Store
     publication — see the project skill/tracker), a second, older bug
     surfaced: BXGY eligibility only checked that the cart held at least
     `buyQuantity` matching units — e.g. "Buy 1, get 2nd item 25% off"
     treated a SINGLE unit in cart as already meeting the requirement
     (floor(1/1) >= 1), when the real Shopify BxGy discount needs
     buyQuantity + getQuantity units present (1 to buy + 1 to discount) to
     allocate anything at all. This made a single-item cart show the 25%
     tier as the "best" eligible deal and auto-apply/Apply it — Shopify
     would attach the code but allocate ₹0, since there was no separate
     "get" item to discount, looking exactly like "Apply did nothing".
     Fixed: eligibility, progress and the "add N more" message now all key
     off the combined buyQuantity+getQuantity unit size, so a 1-item cart
     correctly shows only the no-minimum single-item tier (PUJA15) as
     eligible, and the BXGY tiers correctly show "Add 1 more to unlock…".
*/
(function () {
  var CONTEXTS = ['page', 'drawer'];
  var state = {}; // per-context: {wasOpen, openCardCode, hasNudged, widgetVisible, lastActionableCount, visibilityWatchStarted, lastRoot}

  function getState(ctx) {
    if (!state[ctx]) {
      var hasNudged = false;
      try { hasNudged = sessionStorage.getItem('bt_cp_nudged_' + ctx) === '1'; } catch (e) {}
      state[ctx] = {
        wasOpen: false,
        openCardCode: null,
        hasNudged: hasNudged,
        widgetVisible: false,
        lastActionableCount: 0,
        visibilityWatchStarted: false,
        lastRoot: null,
        pubsubBound: false
      };
    }
    return state[ctx];
  }

  function bindDetailsToggle(ctx, details) {
    if (!details || details.dataset.btBound === 'true') return;
    details.dataset.btBound = 'true';
    details.addEventListener('toggle', function () {
      getState(ctx).wasOpen = details.open;
    });
  }

  function maybeTriggerNudge(ctx) {
    var s = getState(ctx);
    if (s.hasNudged || !s.widgetVisible || s.lastActionableCount <= 0) return;
    var details = document.getElementById('bt-cp-details-' + ctx);
    if (!details || details.open) return;
    var summary = details.querySelector('summary');
    if (!summary) return;
    s.hasNudged = true;
    try { sessionStorage.setItem('bt_cp_nudged_' + ctx, '1'); } catch (e) {}
    summary.classList.add('bt-cp-nudge');
    summary.addEventListener('animationend', function handler() {
      summary.classList.remove('bt-cp-nudge');
      summary.removeEventListener('animationend', handler);
    });
  }

  function markWidgetVisible(ctx) {
    var s = getState(ctx);
    if (s.widgetVisible) return;
    s.widgetVisible = true;
    maybeTriggerNudge(ctx);
  }

  /* Visibility signal per context.
     - 'page': the widget is on-screen as soon as the page itself has loaded.
     - 'drawer': Dawn's cart-drawer.js opens the drawer by adding an "active"
       class to the <cart-drawer> custom element. We watch that exact class
       change via MutationObserver — set up here, at real script-load time,
       so it is armed BEFORE the drawer ever opens, including the very first
       time (empty cart -> first add). */
  function setupVisibilityWatch(ctx) {
    var s = getState(ctx);
    if (s.visibilityWatchStarted) return;
    s.visibilityWatchStarted = true;
    if (ctx !== 'drawer') {
      markWidgetVisible(ctx);
      return;
    }
    var drawerEl = document.querySelector('cart-drawer');
    if (!drawerEl) { s.visibilityWatchStarted = false; return; } /* not in DOM yet — retry later via the generic watcher */
    if (drawerEl.classList.contains('active')) { markWidgetVisible(ctx); return; }
    try {
      var mo2 = new MutationObserver(function () {
        if (drawerEl.classList.contains('active')) {
          markWidgetVisible(ctx);
          mo2.disconnect();
        }
      });
      mo2.observe(drawerEl, { attributes: true, attributeFilter: ['class'] });
    } catch (e) { markWidgetVisible(ctx); }
  }

  function fetchCart() { return fetch('/cart.js').then(function (r) { return r.json(); }); }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency || 'INR', maximumFractionDigits: 0 }).format(cents / 100);
    } catch (e) { return '₹' + Math.round(cents / 100); }
  }

  function collectIds(promo) {
    var ids = (promo.cartProductIds || []).slice();
    (promo.productIds || []).forEach(function (id) { if (ids.indexOf(id) === -1) ids.push(id); });
    (promo.recommendProducts || []).forEach(function (p) { if (p && p.id && ids.indexOf(p.id) === -1) ids.push(p.id); });
    return ids;
  }

  function computeMatch(cart, ids, scope) {
    var qty = 0, subtotal = 0;
    cart.items.forEach(function (item) {
      if (scope === 'all' || (ids && ids.indexOf(item.product_id) > -1)) {
        qty += item.quantity;
        subtotal += (item.original_line_price != null ? item.original_line_price : item.line_price);
      }
    });
    return { qty: qty, subtotal: subtotal };
  }

  function appliedCodes(cart) {
    var seen = {};
    (cart.discount_codes || []).forEach(function (d) {
      if (d && typeof d === 'object' && d.applicable === false) return;
      var c = typeof d === 'string' ? d : (d && d.code) || '';
      if (c) seen[String(c).toUpperCase()] = true;
    });
    (cart.cart_level_discount_applications || []).forEach(function (a) {
      if (a && a.title) seen[String(a.title).toUpperCase()] = true;
    });
    (cart.items || []).forEach(function (item) {
      (item.discounts || []).forEach(function (d) {
        if (d && d.title) seen[String(d.title).toUpperCase()] = true;
      });
      (item.line_level_discount_allocations || []).forEach(function (a) {
        var t = a && a.discount_application && (a.discount_application.title || a.discount_application.key);
        if (t) seen[String(t).toUpperCase()] = true;
      });
    });
    return Object.keys(seen);
  }

  function appliedSavings(cart, code) {
    var cents = 0;
    code = (code || '').toUpperCase();
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (a) {
        var t = a && a.discount_application && (a.discount_application.title || a.discount_application.key);
        if (t && String(t).toUpperCase() === code) { cents += a.amount || 0; }
      });
    });
    (cart.cart_level_discount_applications || []).forEach(function (a) {
      if (a && a.title && String(a.title).toUpperCase() === code) { cents += a.total_allocated_amount || 0; }
    });
    if (cents === 0 && appliedCodes(cart).length === 1) { cents = cart.total_discount || 0; }
    return cents;
  }

  function appliedManualCodes(cart) {
    var seen = {};
    (cart.discount_codes || []).forEach(function (d) {
      if (d && typeof d === 'object' && d.applicable === false) return;
      var c = typeof d === 'string' ? d : (d && d.code) || '';
      if (c) seen[String(c).toUpperCase()] = true;
    });
    (cart.cart_level_discount_applications || []).forEach(function (a) {
      if (a && a.title && (!a.type || a.type === 'discount_code')) seen[String(a.title).toUpperCase()] = true;
    });
    (cart.items || []).forEach(function (item) {
      (item.line_level_discount_allocations || []).forEach(function (a) {
        var app = a && a.discount_application;
        if (app && (app.title || app.key) && (!app.type || app.type === 'discount_code')) {
          seen[String(app.title || app.key).toUpperCase()] = true;
        }
      });
    });
    return Object.keys(seen);
  }

  function notifyThemeCartUpdate(cartData) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bt-cart-promotions', cartData: cartData });
      }
    } catch (e) { }
  }

  function refreshThemeCartSections() {
    var cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
    var itemsEl = document.getElementById('main-cart-items');
    var footerEl = document.getElementById('main-cart-footer');
    var itemsId = (itemsEl && itemsEl.dataset && itemsEl.dataset.id) ? itemsEl.dataset.id : null;
    var footerId = (footerEl && footerEl.dataset && footerEl.dataset.id) ? footerEl.dataset.id : null;
    var hasDrawer = !!document.querySelector('cart-drawer');
    var ids = ['cart-icon-bubble'];
    if (itemsId) ids.push(itemsId);
    if (footerId) ids.push(footerId);
    if (hasDrawer) ids.push('cart-drawer');
    fetch(cartUrl + '?sections=' + ids.join(','))
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var parser = new DOMParser();
        if (itemsId && sections[itemsId]) {
          var doc = parser.parseFromString(sections[itemsId], 'text/html');
          var src = doc.querySelector('cart-items');
          var dest = document.querySelector('cart-items');
          if (src && dest) { dest.className = src.className; dest.innerHTML = src.innerHTML; }
        }
        if (footerId && sections[footerId]) {
          var docF = parser.parseFromString(sections[footerId], 'text/html');
          var srcF = docF.getElementById('main-cart-footer');
          var destF = document.getElementById('main-cart-footer');
          if (srcF && destF) { destF.className = srcF.className; destF.innerHTML = srcF.innerHTML; }
        }
        if (sections['cart-icon-bubble']) {
          var docB = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
          var srcB = docB.querySelector('.shopify-section');
          var destB = document.getElementById('cart-icon-bubble');
          if (srcB && destB) { destB.innerHTML = srcB.innerHTML; }
        }
        if (sections['cart-drawer']) {
          var docD = parser.parseFromString(sections['cart-drawer'], 'text/html');
          var srcD = docD.querySelector('#CartDrawer');
          var destD = document.querySelector('#CartDrawer');
          if (srcD && destD) { destD.className = srcD.className; destD.innerHTML = srcD.innerHTML; }
        }
      })
      .catch(function () { /* non-fatal: theme pub/sub was already notified */ });
  }

  function computeState(promo, cart, appliedSet) {
    var recs = promo.recommendProducts || [];
    var applied = appliedSet.indexOf((promo.code || '').toUpperCase()) > -1;
    var hasAny = false, meetsMin = false, progressPct = 0, needMsg = '';

    if (promo.type === 'bxgy') {
      var buyIds = (promo.buyProductIds || []).concat(promo.cartBuyProductIds || []);
      var buyMatch = computeMatch(cart, buyIds, 'collection');
      hasAny = buyMatch.qty > 0;
      /* v3.1 fix: a BxGy tier needs buyQuantity + getQuantity units present
         to allocate anything — checking buyQuantity alone (the old logic)
         let a cart with only 1 matching unit show as already "eligible"
         for e.g. "Buy 1, get 2nd item 25% off", when Shopify's own BxGy
         discount would attach the code but discount nothing (no separate
         "get" item exists yet). */
      var unitSize = (promo.buyQuantity || 0) + (promo.getQuantity || 0);
      var sets = unitSize ? Math.floor(buyMatch.qty / unitSize) : 0;
      meetsMin = sets >= 1;
      var remainder = unitSize ? (buyMatch.qty % unitSize) : 0;
      var remainderNeeded = unitSize ? (unitSize - remainder) : 0;
      progressPct = unitSize ? Math.min(100, (remainder / unitSize) * 100) : 0;
      if (meetsMin) { progressPct = 100; remainderNeeded = 0; }
      needMsg = meetsMin
        ? (sets * (promo.getQuantity || 1)) + ' item(s) qualify for ' + promo.getDiscountPercentage + '% off!'
        : 'Add ' + remainderNeeded + ' more to unlock Buy ' + promo.buyQuantity + ' Get ' + promo.getQuantity + ' at ' + promo.getDiscountPercentage + '% off';
    } else {
      var match = computeMatch(cart, collectIds(promo), promo.scope);
      hasAny = promo.scope === 'all' ? (cart.item_count > 0) : (match.qty > 0);
      var qtyOk = !promo.minQuantity || match.qty >= promo.minQuantity;
      var subOk = !promo.minSubtotal || match.subtotal >= (promo.minSubtotal * 100);
      meetsMin = qtyOk && subOk;
      if (promo.minQuantity) {
        progressPct = Math.min(100, (match.qty / promo.minQuantity) * 100);
        needMsg = meetsMin ? '' : ('Add ' + (promo.minQuantity - match.qty) + ' more item(s) from this edit to unlock ' + promo.value + '% off');
      } else if (promo.minSubtotal) {
        progressPct = Math.min(100, (match.subtotal / (promo.minSubtotal * 100)) * 100);
        needMsg = meetsMin ? '' : ('Add ' + money(Math.max(0, (promo.minSubtotal * 100) - match.subtotal), cart.currency) + ' more from this edit to unlock ' + promo.value + '% off');
      } else {
        meetsMin = hasAny;
        needMsg = meetsMin ? '' : 'Add a product from this collection to use this code';
      }
    }

    var st = applied ? 'applied' : (meetsMin ? 'eligible' : (hasAny ? 'progress' : 'none'));
    if (st === 'applied') {
      var saved = appliedSavings(cart, promo.code);
      needMsg = saved > 0 ? ('Applied — you\'re saving ' + money(saved, cart.currency)) : 'Discount applied to your cart';
      progressPct = 100;
    } else if (st === 'eligible') {
      var pct = promo.value || promo.getDiscountPercentage;
      if (!needMsg) { needMsg = 'Unlocked — apply ' + promo.code + (pct ? (' to get ' + pct + '% off') : ''); }
      progressPct = 100;
    }
    var disabled = !(st === 'eligible' || st === 'applied');
    return { state: st, disabled: disabled, progressPct: progressPct, needMsg: needMsg, recs: recs, applied: applied };
  }

  function buildCard(ctx, promo, info, isOpenDefault) {
    var card = document.createElement('div');
    var st = info.state, disabled = info.disabled, progressPct = info.progressPct, needMsg = info.needMsg, recs = info.recs, applied = info.applied;
    card.className = 'bt-cp-card is-' + st + (isOpenDefault ? ' is-open' : '');
    card.dataset.code = promo.code;

    var badgeText = {
      applied: '✓ Applied',
      eligible: '✓ Eligible',
      progress: 'Almost there',
      none: 'Add items to use'
    }[st];

    var primaryBtn = applied
      ? '<button type="button" class="bt-cp-apply is-remove" data-action="remove" data-code="' + promo.code + '">Remove</button>'
      : '<button type="button" class="bt-cp-apply" data-action="apply" data-code="' + promo.code + '"' + (disabled ? ' disabled' : '') + '>Apply ' + promo.code + '</button>';

    var copyBtn = '<button type="button" class="bt-cp-copy" data-code="' + promo.code + '"' + (disabled ? ' disabled' : '') + '>Copy code ' + promo.code + '</button>';

    var viewAllUrl = promo.type === 'bxgy' ? promo.getCollectionUrl : promo.collectionUrl;
    var recsHtml = '';

    if (ctx === 'drawer') {
      if (viewAllUrl) {
        recsHtml = '<div class="bt-cp-recs-head bt-cp-recs-head--center">' +
            '<a class="bt-cp-viewall bt-cp-viewall--full" href="' + viewAllUrl + '">View All Eligible Products</a>' +
          '</div>';
      }
    } else {
      var showRecsCarousel = !applied && recs.length > 0;
      var viewAllLink = viewAllUrl ? '<a class="bt-cp-viewall" href="' + viewAllUrl + '">View All →</a>' : '';
      if (showRecsCarousel || viewAllLink) {
        recsHtml = '<div class="bt-cp-recs-head">' +
            (showRecsCarousel ? '<span>You may also like</span>' : '<span></span>') +
            viewAllLink +
          '</div>' +
          (showRecsCarousel
            ? '<div class="bt-cp-recs">' + recs.map(function (p) {
                return '<div class="bt-cp-rec">' +
                  '<img src="' + p.image + '" alt="" loading="lazy">' +
                  '<span class="bt-cp-rec-title">' + p.title + '</span>' +
                  '<span>' + p.price + '</span>' +
                  '<button type="button" class="bt-cp-rec-add" data-variant-id="' + p.variantId + '">+ Quick add</button>' +
                  '</div>';
              }).join('') + '</div>'
            : '');
      }
    }

    var glanceMsgClass = (st === 'applied' || st === 'eligible') ? ' bt-cp-needmsg--good' : '';
    var glanceHtml = '<div class="bt-cp-glance">' +
        '<div class="bt-cp-progress"><div class="bt-cp-progress-fill" style="width:' + progressPct + '%"></div></div>' +
        (needMsg ? '<div class="bt-cp-sub bt-cp-needmsg' + glanceMsgClass + '">' + needMsg + '</div>' : '') +
      '</div>';

    card.innerHTML =
      '<button type="button" class="bt-cp-header">' +
        '<span class="bt-cp-icon">' + (promo.icon || '🎉') + '</span>' +
        '<span class="bt-cp-label">' + promo.label + '</span>' +
        '<span class="bt-cp-badge">' + badgeText + '</span>' +
        '<svg class="bt-cp-chevron" width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      glanceHtml +
      '<div class="bt-cp-body">' +
        (promo.sublabel ? '<div class="bt-cp-sub">' + promo.sublabel + '</div>' : '') +
        '<div class="bt-cp-actions">' + primaryBtn + copyBtn + '</div>' +
        recsHtml +
      '</div>';

    return card;
  }

  function groupPromos(withState) {
    var groups = {};
    var out = [];
    withState.forEach(function (item) {
      var tg = item.promo.tierGroup;
      if (tg) {
        if (!groups[tg]) { groups[tg] = { isGroup: true, tierGroup: tg, items: [] }; out.push(groups[tg]); }
        groups[tg].items.push(item);
      } else {
        out.push(item);
      }
    });
    out.forEach(function (entry) {
      if (!entry.isGroup) return;
      entry.items.sort(function (a, b) { return (a.promo.value || 0) - (b.promo.value || 0); });
      var eligibleOrApplied = entry.items.filter(function (i) { return i.info.state === 'eligible' || i.info.state === 'applied'; });
      var best = eligibleOrApplied.length
        ? eligibleOrApplied.reduce(function (a, b) { return (b.promo.value > a.promo.value) ? b : a; })
        : entry.items[0];
      entry.best = best;
      entry.state = (best.info.state === 'applied')
        ? 'applied'
        : (eligibleOrApplied.length ? 'eligible' : (entry.items.some(function (i) { return i.info.state === 'progress'; }) ? 'progress' : 'none'));
      entry.next = null;
      for (var i = 0; i < entry.items.length; i++) {
        var it = entry.items[i];
        if (it.promo.value > best.promo.value && it.info.state !== 'applied' && it.info.state !== 'eligible') { entry.next = it; break; }
      }
      var withLabel = entry.items.filter(function (i) { return i.promo.groupLabel; })[0];
      entry.groupLabel = withLabel ? withLabel.promo.groupLabel : 'Bundle rewards';
    });
    return out;
  }

  function buildTierCard(ctx, entry, isOpenDefault) {
    var card = document.createElement('div');
    var st = entry.state, best = entry.best, next = entry.next;
    var applied = best.info.applied;
    var disabled = !(st === 'eligible' || st === 'applied');
    card.className = 'bt-cp-card bt-cp-tier is-' + st + (isOpenDefault ? ' is-open' : '');
    card.dataset.code = best.promo.code;

    var badgeText = {
      applied: '✓ ' + best.promo.value + '% Applied',
      eligible: '✓ Best: ' + best.promo.value + '% off',
      progress: 'Almost there',
      none: 'Add items to use'
    }[st];

    var primaryBtn = applied
      ? '<button type="button" class="bt-cp-apply is-remove" data-action="remove" data-code="' + best.promo.code + '">Remove</button>'
      : '<button type="button" class="bt-cp-apply" data-action="apply" data-code="' + best.promo.code + '"' + (disabled ? ' disabled' : '') + '>Apply best — ' + best.promo.code + '</button>';
    var copyBtn = '<button type="button" class="bt-cp-copy" data-code="' + best.promo.code + '"' + (disabled ? ' disabled' : '') + '>Copy code ' + best.promo.code + '</button>';

    var msHtml = '<div class="bt-cp-ms">' + entry.items.map(function (item) {
      var s2 = item.info.state;
      var done = s2 === 'applied' || s2 === 'eligible';
      var fill = done ? 100 : (item.info.progressPct || 0);
      var nodeCls = s2 === 'applied' ? ' bt-cp-ms-node--applied' : (done ? ' bt-cp-ms-node--done' : '');
      return '<div class="bt-cp-ms-seg"><div class="bt-cp-ms-fill" style="width:' + fill + '%"></div></div>' +
        '<div class="bt-cp-ms-node' + nodeCls + '">' + (done ? '✓' : '') +
          '<span class="bt-cp-ms-label">' + item.promo.value + '%' + (s2 === 'applied' ? ' on' : '') + '</span>' +
        '</div>';
    }).join('') + '</div>';

    var lineFor = function (item) {
      var s2 = item.info.state;
      var done = s2 === 'applied' || s2 === 'eligible';
      var msg;
      if (s2 === 'applied') { msg = item.info.needMsg; }
      else if (s2 === 'eligible') { msg = (item === best) ? 'Unlocked — your best deal' : 'Unlocked'; }
      else { msg = item.info.needMsg; }
      return '<div class="bt-cp-tierline' + (done ? ' bt-cp-tierline--done' : '') + '">' +
        '<span class="bt-cp-tierpct">' + item.promo.value + '%</span><span>' + msg + '</span></div>';
    };
    var tierLines;
    if (ctx === 'drawer') {
      var appliedItem = entry.items.filter(function (i) { return i.info.state === 'applied'; })[0];
      tierLines = (appliedItem ? lineFor(appliedItem) : (st === 'eligible' ? lineFor(best) : '')) +
        (next ? lineFor(next) : '');
      if (!tierLines) { tierLines = lineFor(entry.items[0]); }
    } else {
      tierLines = entry.items.map(lineFor).join('');
    }
    var glanceHtml = '<div class="bt-cp-glance">' + msHtml + tierLines + '</div>';

    var recs = best.info.recs || [];
    var viewAllUrl = best.promo.collectionUrl;
    var recsHtml = '';
    var showRecsCarousel = recs.length > 0 && (!applied || !!next);
    var recsHead = (applied && next) ? 'Add more to reach your next milestone' : 'You may also like';
    if (ctx === 'drawer') {
      if (viewAllUrl) {
        recsHtml = '<div class="bt-cp-recs-head bt-cp-recs-head--center">' +
            '<a class="bt-cp-viewall bt-cp-viewall--full" href="' + viewAllUrl + '">View All Eligible Products</a>' +
          '</div>';
      }
    } else {
      var viewAllLink = viewAllUrl ? '<a class="bt-cp-viewall" href="' + viewAllUrl + '">View All →</a>' : '';
      if (showRecsCarousel || viewAllLink) {
        recsHtml = '<div class="bt-cp-recs-head">' +
            (showRecsCarousel ? '<span>' + recsHead + '</span>' : '<span></span>') +
            viewAllLink +
          '</div>' +
          (showRecsCarousel
            ? '<div class="bt-cp-recs">' + recs.map(function (p) {
                return '<div class="bt-cp-rec">' +
                  '<img src="' + p.image + '" alt="" loading="lazy">' +
                  '<span class="bt-cp-rec-title">' + p.title + '</span>' +
                  '<span>' + p.price + '</span>' +
                  '<button type="button" class="bt-cp-rec-add" data-variant-id="' + p.variantId + '">+ Quick add</button>' +
                  '</div>';
              }).join('') + '</div>'
            : '');
      }
    }

    card.innerHTML =
      '<button type="button" class="bt-cp-header">' +
        '<span class="bt-cp-icon">' + (best.promo.icon || '🎁') + '</span>' +
        '<span class="bt-cp-label">' + entry.groupLabel + '</span>' +
        '<span class="bt-cp-badge">' + badgeText + '</span>' +
        '<svg class="bt-cp-chevron" width="12" height="8" viewBox="0 0 12 8" fill="none"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      glanceHtml +
      '<div class="bt-cp-body">' +
        (best.promo.sublabel ? '<div class="bt-cp-sub">' + best.promo.sublabel + '</div>' : '') +
        '<div class="bt-cp-actions">' + primaryBtn + copyBtn + '</div>' +
        recsHtml +
      '</div>';

    return card;
  }

  function maybeAutoApplyBest(ctx, entries, cart, root, promos) {
    if (window.__btCpAutoApplyBusy) return;
    window.__btCpAutoTried = window.__btCpAutoTried || {};
    var manualCodes = appliedManualCodes(cart);
    var target = null;
    entries.forEach(function (entry) {
      if (!entry.isGroup || target) return;
      var noauto = false;
      try { noauto = sessionStorage.getItem('bt_cp_noauto_' + entry.tierGroup) === '1'; } catch (e) {}
      if (noauto) return;
      var groupCodes = entry.items.map(function (i) { return (i.promo.code || '').toUpperCase(); });
      var bestEligible = null;
      entry.items.forEach(function (i) {
        if (i.info.state === 'eligible' || i.info.state === 'applied') {
          if (!bestEligible || (i.promo.value || 0) > (bestEligible.promo.value || 0)) bestEligible = i;
        }
      });
      if (!bestEligible || bestEligible.info.state === 'applied') return;
      if (window.__btCpAutoTried[(bestEligible.promo.code || '').toUpperCase()]) return;
      var outside = manualCodes.filter(function (c) { return groupCodes.indexOf(c) === -1; });
      if (outside.length > 0) return;
      target = bestEligible;
    });
    if (!target) return;
    var code = target.promo.code;
    window.__btCpAutoApplyBusy = true;
    window.__btCpAutoTried[(code || '').toUpperCase()] = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code })
    }).then(function (r) { return r.json(); }).then(function (newCart) {
      window.__btCpAutoApplyBusy = false;
      notifyThemeCartUpdate(newCart);
      render(ctx, newCart, root, promos);
      refreshThemeCartSections();
    }).catch(function () {
      window.__btCpAutoApplyBusy = false;
    });
  }

  function render(ctx, cart, root, promos) {
    var s = getState(ctx);
    root.innerHTML = '';
    var appliedSet = appliedCodes(cart);
    var rankOrder = { applied: 0, eligible: 1, progress: 2, none: 3 };
    var withState = promos.map(function (promo) {
      return { promo: promo, info: computeState(promo, cart, appliedSet) };
    });
    var entries = groupPromos(withState);
    entries.sort(function (a, b) {
      var sa = a.isGroup ? a.state : a.info.state;
      var sb = b.isGroup ? b.state : b.info.state;
      var ra = rankOrder[sa], rb = rankOrder[sb];
      if (ra !== rb) return ra - rb;
      var pa = a.isGroup ? Math.min.apply(null, a.items.map(function (i) { return i.promo.priority || 99; })) : (a.promo.priority || 99);
      var pb = b.isGroup ? Math.min.apply(null, b.items.map(function (i) { return i.promo.priority || 99; })) : (b.promo.priority || 99);
      return pa - pb;
    });

    var actionable = entries.filter(function (e) {
      var st = e.isGroup ? e.state : e.info.state;
      return st === 'eligible' || st === 'applied';
    });
    s.lastActionableCount = actionable.length;
    maybeTriggerNudge(ctx);
    var countEl = document.getElementById('bt-cp-summary-count-' + ctx);
    if (countEl) { countEl.textContent = actionable.length > 0 ? '(' + actionable.length + ')' : ''; }

    var maxShown = (ctx === 'drawer') ? 2 : entries.length;
    var toShow = entries.slice(0, maxShown);

    toShow.forEach(function (entry) {
      var code = entry.isGroup ? entry.best.promo.code : entry.promo.code;
      var isOpenDefault = (s.openCardCode !== null && code === s.openCardCode);
      var card = entry.isGroup ? buildTierCard(ctx, entry, isOpenDefault) : buildCard(ctx, entry.promo, entry.info, isOpenDefault);
      root.appendChild(card);
    });

    if (entries.length > toShow.length) {
      var seeAll = document.createElement('a');
      seeAll.className = 'bt-cp-seeall';
      seeAll.href = ((window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart');
      seeAll.textContent = 'See all ' + entries.length + ' offers →';
      seeAll.addEventListener('click', function (e) {
        if (window.BtWelcomePopup && typeof window.BtWelcomePopup.openOffers === 'function') {
          e.preventDefault();
          window.BtWelcomePopup.openOffers();
        }
      });
      root.appendChild(seeAll);
    }

    root.querySelectorAll('.bt-cp-header').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('.bt-cp-card');
        var wasCardOpen = card.classList.contains('is-open');
        root.querySelectorAll('.bt-cp-card.is-open').forEach(function (c) { c.classList.remove('is-open'); });
        if (!wasCardOpen) {
          card.classList.add('is-open');
          s.openCardCode = card.dataset.code || null;
        } else {
          s.openCardCode = null;
        }
      });
    });

    root.querySelectorAll('.bt-cp-copy').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var code = btn.getAttribute('data-code');
        navigator.clipboard.writeText(code).then(function () {
          btn.textContent = 'Copied ✓';
          btn.classList.add('copied');
          setTimeout(function () { btn.textContent = 'Copy code ' + code; btn.classList.remove('copied'); }, 1800);
        });
      });
    });

    root.querySelectorAll('[data-action="apply"], [data-action="remove"]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var action = btn.getAttribute('data-action');
        var code = btn.getAttribute('data-code');
        if (action === 'remove') {
          var removedPromo = promos.filter(function (pp) { return (pp.code || '').toUpperCase() === (code || '').toUpperCase(); })[0];
          if (removedPromo && removedPromo.tierGroup) {
            try { sessionStorage.setItem('bt_cp_noauto_' + removedPromo.tierGroup, '1'); } catch (e) {}
          }
        }
        var originalText = btn.textContent;
        btn.disabled = true;
        btn.textContent = action === 'apply' ? 'Applying…' : 'Removing…';
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: action === 'apply' ? code : '' })
        }).then(function (r) { return r.json(); }).then(function (newCart) {
          notifyThemeCartUpdate(newCart);
          render(ctx, newCart, root, promos);
          refreshThemeCartSections();
        }).catch(function () {
          btn.disabled = false;
          btn.textContent = originalText;
        });
      });
    });

    root.querySelectorAll('.bt-cp-rec-add').forEach(function (btn) {
      btn.addEventListener('click', function () {
        btn.disabled = true;
        btn.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: btn.getAttribute('data-variant-id'), quantity: 1 })
        }).then(function (r) {
          if (!r.ok) { throw new Error('Add to cart failed: ' + r.status); }
          return fetchCart();
        }).then(function (newCart) {
          notifyThemeCartUpdate(newCart);
          render(ctx, newCart, root, promos);
          refreshThemeCartSections();
        }).catch(function () {
          btn.disabled = false;
          btn.textContent = '+ Quick add';
        });
      });
    });

    maybeAutoApplyBest(ctx, entries, cart, root, promos);
  }

  function refreshContext(ctx) {
    var s = getState(ctx);
    var root = document.getElementById('bt-cart-promo-widget-' + ctx);
    var dataEl = document.getElementById('bt-cart-promo-data-' + ctx);
    var details = document.getElementById('bt-cp-details-' + ctx);
    if (!root || !dataEl) return false; /* markup not in the DOM yet for this context */
    s.lastRoot = root;
    bindDetailsToggle(ctx, details);
    if (details && s.wasOpen) { details.open = true; }
    var promos;
    try { promos = JSON.parse(dataEl.textContent); } catch (e) { return true; }
    if (!promos.length) return true;
    fetchCart().then(function (cart) { render(ctx, cart, root, promos); });
    return true;
  }

  function refreshAll() {
    CONTEXTS.forEach(function (ctx) {
      if (refreshContext(ctx)) setupVisibilityWatch(ctx);
    });
  }

  /* Dawn loads pubsub.js/cart.js with defer, so `subscribe` may not exist the
     instant this file parses — retry at DOMContentLoaded/load too. */
  var pubsubBound = false;
  function tryBindPubsub() {
    if (pubsubBound) return;
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { setTimeout(refreshAll, 50); });
        pubsubBound = true;
      }
    } catch (e) { }
  }

  document.addEventListener('cart:refresh', refreshAll);
  document.addEventListener('cart:update', refreshAll);
  tryBindPubsub();
  document.addEventListener('DOMContentLoaded', function () { tryBindPubsub(); refreshAll(); });
  window.addEventListener('load', function () { tryBindPubsub(); refreshAll(); });

  /* Safety net for the remove link and +/- steppers (click has no change
     event; the cart page re-renders only .js-contents, outside which this
     widget's container sits). */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('cart-remove-button') || t.closest('button[name="plus"], button[name="minus"]')) {
      setTimeout(refreshAll, 700);
      setTimeout(refreshAll, 1600);
    }
  }, true);
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.matches && (t.matches('input[name="updates[]"]') || t.closest('quantity-input'))) {
      setTimeout(refreshAll, 600);
    }
  });

  /* THE core fix: this observer is armed from real page-load time, not from
     inside the swappable drawer/cart markup — so it reliably catches the
     very FIRST time a widget root appears (e.g. drawer goes from empty-cart,
     where the promo snippet isn't rendered at all, to holding its first
     item via an AJAX innerHTML swap), as well as every later swap. */
  if (typeof MutationObserver !== 'undefined') {
    try {
      var mo = new MutationObserver(function () {
        CONTEXTS.forEach(function (ctx) {
          var s = getState(ctx);
          var currentRoot = document.getElementById('bt-cart-promo-widget-' + ctx);
          if (currentRoot && currentRoot !== s.lastRoot) {
            refreshContext(ctx);
            setupVisibilityWatch(ctx);
          }
        });
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) { }
  }

  /* Kick things off immediately too, in case both contexts are already in
     the initial HTML (e.g. loading the cart page directly with items in
     cart) — no need to wait for DOMContentLoaded in that case. */
  refreshAll();
})();
